import { dehydrate, QueryClient } from '@tanstack/react-query';
import { STATUS_CODE } from '@kc-console/core';

import { isServerTraceEnabled } from '../../utils/serverTrace/sampling';
import {
  addServerTraceGuardsDuration,
  appendServerTraceResponseHeaders,
  beginServerTraceExecution,
} from '../../utils/serverTrace/store';

import type { GetServerSidePropsContext, NextPageContext } from 'next';
import type { SSRConfig } from 'next-i18next';
import type { BaseState } from '@kc-console/context';
import type { Guard } from '../../types';
import type { ServerTraceExecutionResult } from '../../utils/serverTrace/types';

export interface ErrorPageProps extends SSRConfig {
  dehydratedState: unknown;
  state: { status: number };
  statusCode: number;
}

type ExecuteInitialPropsGuardsParams = {
  context: NextPageContext;
  guards: Guard<BaseState>;
  state: BaseState;
};

function adaptToServerSideContext(context: NextPageContext): GetServerSidePropsContext {
  const { req, res, query, asPath } = context;

  return {
    req: req!,
    res: res!,
    query: query || {},
    resolvedUrl: asPath || '',
    params: undefined,
    preview: undefined,
    previewData: undefined,
    draftMode: undefined,
    locale: undefined,
    locales: undefined,
    defaultLocale: undefined,
  } as GetServerSidePropsContext;
}

export const executeInitialPropsGuards = async ({
  context,
  guards,
  state: initialState,
}: ExecuteInitialPropsGuardsParams): Promise<ErrorPageProps> => {
  const { res, err } = context;
  const statusCode = res?.statusCode ?? err?.statusCode ?? 500;

  if (typeof window !== 'undefined' || !context.req) {
    return {
      statusCode,
      dehydratedState: null,
      state: { status: statusCode },
      _nextI18Next: undefined,
    } as ErrorPageProps;
  }

  const queryClient = new QueryClient();
  const adaptedContext = adaptToServerSideContext(context);
  const execution = beginServerTraceExecution(adaptedContext, 'error');
  let executionResult: ServerTraceExecutionResult = {
    outcome: 'rejected',
    statusCode,
    service: initialState.serviceKeyword,
  };
  const traceEnabled = isServerTraceEnabled();

  try {
    const guardsStartedAt = traceEnabled
      ? (globalThis.performance?.now() ?? Date.now())
      : undefined;
    let state: Partial<BaseState>;

    try {
      state = await guards({
        context: adaptedContext,
        state: initialState,
        queryClient,
      });
    } finally {
      if (guardsStartedAt !== undefined) {
        const guardsSettledAt = globalThis.performance?.now() ?? Date.now();
        addServerTraceGuardsDuration(adaptedContext.req, guardsSettledAt - guardsStartedAt);
      }
    }

    executionResult = {
      outcome:
        statusCode >= STATUS_CODE.BAD_REQUEST || state.status !== STATUS_CODE.OK
          ? 'failed_status'
          : 'success',
      statusCode,
      service: state.serviceKeyword,
    };

    return {
      statusCode,
      ...state.ssrConfig,
      dehydratedState: dehydrate(queryClient),
      state: { status: state.status || STATUS_CODE.OK },
    };
  } catch (error) {
    console.error('[executeInitialPropsGuards] Failed to execute guards:', error);
    executionResult = {
      outcome: 'rejected',
      statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      service: initialState.serviceKeyword,
    };

    return {
      statusCode,
      dehydratedState: dehydrate(queryClient),
      state: { status: STATUS_CODE.INTERNAL_SERVER_ERROR },
    } as ErrorPageProps;
  } finally {
    execution.end(executionResult);
    appendServerTraceResponseHeaders(adaptedContext);
  }
};
